import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell, Legend,
} from 'recharts'
import { ClipboardList, TrendingUp, Activity, AlertCircle, CalendarClock, Timer } from 'lucide-react'

function rateColor(rate) {
  if (rate >= 70) return '#10b981'
  if (rate >= 40) return '#f59e0b'
  return '#ef4444'
}

function KpiCard({ label, value, icon: Icon, colorClass, pulse, isDarkMode }) {
  return (
    <div className={`rounded-xl p-4 border flex items-center gap-4 ${
      isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
    }`}>
      <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
        {pulse && <span className="absolute inset-0 rounded-full animate-ping opacity-40 bg-red-500" />}
        <Icon className="w-5 h-5 text-white relative z-10" />
      </div>
      <div>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>{value}</p>
        <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{label}</p>
      </div>
    </div>
  )
}

function ChartCard({ title, children, isDarkMode }) {
  return (
    <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
      <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{title}</p>
      {children}
    </div>
  )
}

function buildWeeklyDualData(tasks) {
  const weeks = []
  const now = new Date()
  for (let i = 7; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i * 7)
    const weekStart = new Date(d)
    weekStart.setDate(d.getDate() - d.getDay())
    weekStart.setHours(0, 0, 0, 0)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 6)
    weekEnd.setHours(23, 59, 59, 999)
    weeks.push({
      label: weekStart.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }),
      start: weekStart,
      end: weekEnd,
      created: 0,
      completed: 0,
    })
  }
  tasks.forEach((t) => {
    if (t.created_at) {
      const d = new Date(t.created_at)
      const w = weeks.find((wk) => d >= wk.start && d <= wk.end)
      if (w) w.created++
    }
    if (t.status === 'completed' && t.updated_at) {
      const d = new Date(t.updated_at)
      const w = weeks.find((wk) => d >= wk.start && d <= wk.end)
      if (w) w.completed++
    }
  })
  return weeks.map(({ label, created, completed }) => ({ label, created, completed }))
}

function formatDayLabel(dateStr) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const d = new Date(dateStr)
  d.setHours(0, 0, 0, 0)
  if (d.getTime() === today.getTime()) return 'Today'
  if (d.getTime() === tomorrow.getTime()) return 'Tomorrow'
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric' })
}

const STATUS_COLORS = {
  open:        { light: 'bg-blue-100 text-blue-700',   dark: 'bg-blue-900/40 text-blue-300' },
  in_progress: { light: 'bg-amber-100 text-amber-700', dark: 'bg-amber-900/40 text-amber-300' },
  completed:   { light: 'bg-green-100 text-green-700', dark: 'bg-green-900/40 text-green-300' },
  cancelled:   { light: 'bg-gray-100 text-gray-600',   dark: 'bg-gray-800 text-gray-400' },
}

function severityDot(days) {
  if (days >= 14) return 'bg-red-500'
  if (days >= 7)  return 'bg-orange-500'
  return 'bg-yellow-500'
}

export function TaskDashboard({ tasks, isDarkMode, onSelectTask }) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const weekEnd = new Date(today)
  weekEnd.setDate(today.getDate() + 7)

  const isDone = (t) => t.status === 'completed' || t.status === 'cancelled'

  // ── KPI calculations ──
  const total = tasks.length
  const completed = tasks.filter((t) => t.status === 'completed').length
  const completionRate = total > 0 ? Math.round(completed / total * 100) : 0
  const inProgress = tasks.filter((t) => t.status === 'in_progress').length

  const overdueList = tasks.filter((t) => {
    if (!t.due_date || isDone(t)) return false
    return new Date(t.due_date) < today
  })

  const dueThisWeekList = tasks
    .filter((t) => {
      if (!t.due_date || isDone(t)) return false
      const d = new Date(t.due_date)
      return d >= today && d <= weekEnd
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))

  const openTasks = tasks.filter((t) => t.status === 'open' || t.status === 'in_progress')
  const avgDaysOpen = openTasks.length > 0
    ? Math.round(
        openTasks.reduce((sum, t) => sum + (today - new Date(t.created_at)) / 86400000, 0) / openTasks.length
      )
    : 0

  const rateColorClass = completionRate >= 70 ? 'bg-green-500' : completionRate >= 40 ? 'bg-amber-500' : 'bg-red-500'

  // ── Team performance ──
  const advisorMap = {}
  tasks.forEach((t) => {
    const name = t.advisors?.full_name ?? 'Unassigned'
    if (!advisorMap[name]) advisorMap[name] = { total: 0, completed: 0, overdue: 0 }
    advisorMap[name].total++
    if (t.status === 'completed') advisorMap[name].completed++
    if (t.due_date && !isDone(t) && new Date(t.due_date) < today) advisorMap[name].overdue++
  })
  const advisorData = Object.entries(advisorMap)
    .map(([name, v]) => ({
      name: name.split(' ')[0],
      fullName: name,
      rate: v.total > 0 ? Math.round(v.completed / v.total * 100) : 0,
      total: v.total,
      completed: v.completed,
      overdue: v.overdue,
    }))
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8)

  // ── Weekly dual data ──
  const weeklyData = buildWeeklyDualData(tasks)

  // ── Age distribution ──
  const ageCounts = { '0–7d': 0, '8–14d': 0, '15–30d': 0, '30d+': 0 }
  openTasks.forEach((t) => {
    const days = Math.floor((today - new Date(t.created_at)) / 86400000)
    if (days <= 7)       ageCounts['0–7d']++
    else if (days <= 14) ageCounts['8–14d']++
    else if (days <= 30) ageCounts['15–30d']++
    else                 ageCounts['30d+']++
  })
  const ageData = [
    { label: '0–7d',   count: ageCounts['0–7d'],   fill: '#10b981' },
    { label: '8–14d',  count: ageCounts['8–14d'],  fill: '#f59e0b' },
    { label: '15–30d', count: ageCounts['15–30d'], fill: '#f97316' },
    { label: '30d+',   count: ageCounts['30d+'],   fill: '#ef4444' },
  ]

  // ── Section stacked breakdown ──
  const sectionMap = {}
  tasks.forEach((t) => {
    const sec = t.section || 'Claims'
    if (!sectionMap[sec]) sectionMap[sec] = { open: 0, overdue: 0, completed: 0 }
    if (t.status === 'completed') {
      sectionMap[sec].completed++
    } else if (t.due_date && !isDone(t) && new Date(t.due_date) < today) {
      sectionMap[sec].overdue++
    } else if (!isDone(t)) {
      sectionMap[sec].open++
    }
  })
  const sectionData = Object.entries(sectionMap)
    .map(([name, v]) => ({ name, ...v, _total: v.open + v.overdue + v.completed }))
    .sort((a, b) => b._total - a._total)
    .slice(0, 8)

  // ── Overdue spotlight: top 5 most overdue (oldest due_date first) ──
  const overdueSpotlight = [...overdueList]
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 5)
    .map((t) => ({
      ...t,
      daysOverdue: Math.floor((today - new Date(t.due_date)) / 86400000),
    }))

  // ── Styles ──
  const tickColor    = isDarkMode ? '#94a3b8' : '#6b7280'
  const tooltipStyle = isDarkMode
    ? { background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }
    : { background: '#fff',    border: '1px solid #e5e7eb', color: '#1f2937' }
  const cardBg   = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
  const rowHover = isDarkMode ? 'hover:bg-slate-700/50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'
  const textMuted = isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const textSub   = isDarkMode ? 'text-slate-500' : 'text-gray-400'

  return (
    <div className="p-4 space-y-4 overflow-y-auto">

      {/* ── KPI Row — 6 cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total Tasks"     value={total}                    icon={ClipboardList} colorClass="bg-blue-500"   isDarkMode={isDarkMode} />
        <KpiCard label="Completion Rate" value={`${completionRate}%`}     icon={TrendingUp}    colorClass={rateColorClass} isDarkMode={isDarkMode} />
        <KpiCard label="In Progress"     value={inProgress}               icon={Activity}      colorClass="bg-blue-400"   isDarkMode={isDarkMode} />
        <KpiCard label="Overdue"         value={overdueList.length}       icon={AlertCircle}   colorClass="bg-red-500"    pulse={overdueList.length > 0} isDarkMode={isDarkMode} />
        <KpiCard label="Due This Week"   value={dueThisWeekList.length}   icon={CalendarClock} colorClass="bg-orange-500" isDarkMode={isDarkMode} />
        <KpiCard label="Avg Days Open"   value={`${avgDaysOpen}d`}        icon={Timer}         colorClass="bg-slate-500"  isDarkMode={isDarkMode} />
      </div>

      {/* ── Row 1: Team Performance + Weekly Activity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        <ChartCard title="Team Performance — Completion %" isDarkMode={isDarkMode}>
          {advisorData.length === 0 ? (
            <p className={`text-sm text-center py-8 ${textSub}`}>No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={advisorData} layout="vertical" margin={{ top: 4, right: 44, left: 10, bottom: 4 }}>
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: tickColor }} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: tickColor }} width={64} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(_val, _name, props) => {
                    const p = props.payload
                    return [`${p.completed} completed of ${p.total} total (${p.overdue} overdue)`, 'Rate']
                  }}
                />
                <Bar dataKey="rate" name="Completion %" radius={[0, 4, 4, 0]}>
                  {advisorData.map((entry, i) => (
                    <Cell key={i} fill={rateColor(entry.rate)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Weekly Activity — Created vs Completed (Last 8 Weeks)" isDarkMode={isDarkMode}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weeklyData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#f0f0f0'} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: tickColor }} />
              <YAxis tick={{ fontSize: 10, fill: tickColor }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend formatter={(v) => <span style={{ color: tickColor, fontSize: 11 }}>{v}</span>} />
              <Line type="monotone" dataKey="created"   name="Created"   stroke="#f97316" strokeWidth={2} dot={{ r: 3, fill: '#f97316' }}  activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="completed" name="Completed" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }}  activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Row 2: Age Distribution + Section Breakdown ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        <ChartCard title="Task Age Distribution (Open / In Progress)" isDarkMode={isDarkMode}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ageData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: tickColor }} />
              <YAxis tick={{ fontSize: 10, fill: tickColor }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Tasks" radius={[4, 4, 0, 0]}>
                {ageData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Section Breakdown (Open / Overdue / Completed)" isDarkMode={isDarkMode}>
          {sectionData.length === 0 ? (
            <p className={`text-sm text-center py-8 ${textSub}`}>No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sectionData} margin={{ top: 5, right: 10, left: -20, bottom: 40 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: tickColor }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: tickColor }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend formatter={(v) => <span style={{ color: tickColor, fontSize: 11 }}>{v}</span>} />
                <Bar dataKey="open"      name="Open"      stackId="a" fill="#3b82f6" />
                <Bar dataKey="overdue"   name="Overdue"   stackId="a" fill="#ef4444" />
                <Bar dataKey="completed" name="Completed" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* ── Row 3: Overdue Spotlight + Due This Week ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Overdue Spotlight */}
        <div className={`rounded-xl border p-4 ${cardBg}`}>
          <p className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Overdue Spotlight
            {overdueList.length > 5 && (
              <span className={`text-xs font-normal ${textMuted}`}>(top 5 of {overdueList.length})</span>
            )}
          </p>
          {overdueSpotlight.length === 0 ? (
            <p className={`text-sm text-center py-6 ${textSub}`}>No overdue tasks</p>
          ) : (
            <div className="space-y-2">
              {overdueSpotlight.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onSelectTask?.(t.id)}
                  className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-100'
                  } ${rowHover}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${severityDot(t.daysOverdue)}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{t.title}</p>
                    <p className={`text-xs ${textMuted}`}>{t.advisors?.full_name?.split(' ')[0] ?? '—'}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                    isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                  }`}>{t.section || 'Claims'}</span>
                  <span className="text-xs text-red-500 font-semibold flex-shrink-0 whitespace-nowrap">
                    {t.daysOverdue}d overdue
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Due This Week */}
        <div className={`rounded-xl border p-4 ${cardBg}`}>
          <p className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            Due This Week
          </p>
          {dueThisWeekList.length === 0 ? (
            <p className={`text-sm text-center py-6 ${textSub}`}>Nothing due this week</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {dueThisWeekList.map((t) => {
                const sc = STATUS_COLORS[t.status] || STATUS_COLORS.open
                const initials = (t.advisors?.full_name ?? '?')
                  .split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelectTask?.(t.id)}
                    className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                      isDarkMode ? 'border-slate-700' : 'border-gray-100'
                    } ${rowHover}`}
                  >
                    <span className={`text-xs font-medium w-16 flex-shrink-0 ${textMuted}`}>
                      {formatDayLabel(t.due_date)}
                    </span>
                    <p className={`flex-1 text-sm truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{t.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${isDarkMode ? sc.dark : sc.light}`}>
                      {t.status.replace('_', ' ')}
                    </span>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                    }`}>{initials}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
